// Outfits (docs/OUTFITI_garderoba.md): the survivor's look by armor set. Every set has four
// outfits (Common, Rare, Epic, Legendary) for the male and the female look. An outfit is
// unlocked when the Collection book has all 5 pieces of its set in exactly that rarity
// (Uncommon counts for Common, which it shares its look with); a higher rarity never counts
// for a lower outfit. Cosmetic only: no stats, Power, fights or loot change.
//
// Files (copied from assets/characters/outfits by `pnpm icons:sync`; the PNGs stay in assets):
//   /assets/outfits/{look}/webp/{set}_{rarity}_{1024|512}.webp          full figure, transparent
//   /assets/outfits/{look}/webp/{set}_{rarity}_portrait_{256|128}.webp  head and shoulders
//   /assets/outfits/{look}/anim/{set}_legendary.{webm|mp4}              Legendary loop, the panel's background in it
//   /assets/outfits/{look}/anim/{set}_legendary_poster.webp             its first frame

import { BODY_TYPES, type BodyType, type CharacterSize, type PortraitSize } from './characters';
import { collectionKey, type CollectionState } from './collection';
import { ITEM_TEMPLATES, findItemTemplate } from './content/items';
import { SETS } from './items/sets';
// 11. 10. 2026 (BACKLOG #36): only the sets with pictures have outfits (`OUTFIT_SET_IDS`; the Foundry's came the same day).
import { ARMOR_SLOTS, OUTFIT_SET_IDS, type Appearance, type GearSlot, type Rarity, type SetId } from './types';

export const OUTFIT_RARITIES = ['common', 'rare', 'epic', 'legendary'] as const;
export type OutfitRarity = (typeof OUTFIT_RARITIES)[number];

export interface OutfitRef {
  set: SetId;
  rarity: OutfitRarity;
}

export const OUTFIT_SIZES = [1024, 512] as const satisfies readonly CharacterSize[];
export const OUTFIT_PORTRAIT_SIZES = [256, 128] as const satisfies readonly PortraitSize[];

/** Every outfit, set by set in catalog order, Common to Legendary. */
export const ALL_OUTFITS: OutfitRef[] = OUTFIT_SET_IDS.flatMap((set) => OUTFIT_RARITIES.map((rarity) => ({ set, rarity })));

export const outfitKey = (o: OutfitRef) => `${o.set}:${o.rarity}`;

/** `"rider:rare"` back to an outfit, or null when it is not one. */
export function parseOutfitKey(key: string | null | undefined): OutfitRef | null {
  if (!key) return null;
  const [set, rarity] = key.split(':');
  return (OUTFIT_SET_IDS as readonly string[]).includes(set) && (OUTFIT_RARITIES as readonly string[]).includes(rarity) ? { set: set as SetId, rarity: rarity as OutfitRarity } : null;
}

export function isOutfit(o: unknown): o is OutfitRef {
  const x = o as OutfitRef | null;
  return !!x && typeof x === 'object' && (OUTFIT_SET_IDS as readonly string[]).includes(x.set) && (OUTFIT_RARITIES as readonly string[]).includes(x.rarity);
}

const RARITY_TITLE: Record<OutfitRarity, string> = { common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary' };

/** "Rider · Rare" */
export function outfitName(o: OutfitRef): string {
  return `${SETS[o.set].name} · ${RARITY_TITLE[o.rarity]}`;
}

// ---- Paths: the only place that builds them ---------------------------------------------------

export function outfitUrl(bodyType: BodyType, o: OutfitRef, size: CharacterSize): string {
  return `/assets/outfits/${bodyType}/webp/${o.set}_${o.rarity}_${size}.webp`;
}

export function outfitPortraitUrl(bodyType: BodyType, o: OutfitRef, size: PortraitSize): string {
  return `/assets/outfits/${bodyType}/webp/${o.set}_${o.rarity}_portrait_${size}.webp`;
}

/**
 * 11. 10. 2026 (owner): Legendary outfits whose loop is not made yet; until it comes they show the still picture, as
 * everywhere a video cannot play. Empty since the male Foundry loop came the same day.
 */
export const OUTFIT_ANIM_PENDING: Record<BodyType, readonly SetId[]> = { male: [], female: [] };

/** Whether this look's Legendary outfit of the set has its loop (else the still picture). */
export function outfitAnimated(bodyType: BodyType, set: SetId): boolean {
  return !OUTFIT_ANIM_PENDING[bodyType].includes(set);
}

/** The Legendary loop: VP9 WebM, H.264 MP4 (Safari, iOS) and the first frame. */
export function outfitAnimUrls(bodyType: BodyType, set: SetId): { webm: string; mp4: string; poster: string } {
  const base = `/assets/outfits/${bodyType}/anim/${set}_legendary`;
  return { webm: `${base}.webm`, mp4: `${base}.mp4`, poster: `${base}_poster.webp` };
}

/** Files the client expects (per look: 144 WebP + 3 animation files per animated Legendary), for `pnpm icons:check`. */
export const OUTFIT_FILES: string[] = BODY_TYPES.flatMap((b) => [
  ...ALL_OUTFITS.flatMap((o) => [...OUTFIT_SIZES.map((s) => outfitUrl(b, o, s)), ...OUTFIT_PORTRAIT_SIZES.map((s) => outfitPortraitUrl(b, o, s))]),
  ...OUTFIT_SET_IDS.filter((set) => outfitAnimated(b, set)).flatMap((set) => Object.values(outfitAnimUrls(b, set))),
]);

/** Source files under assets/characters/outfits (per look: 36 PNG + 144 WebP + the animation files). */
export const OUTFIT_SOURCE_FILES: string[] = BODY_TYPES.flatMap((b) => [
  ...ALL_OUTFITS.map((o) => `${b}/${o.set}_${o.rarity}.png`),
  ...OUTFIT_FILES.filter((f) => f.startsWith(`/assets/outfits/${b}/`)).map((f) => f.slice('/assets/outfits/'.length)),
]);

// ---- The rule --------------------------------------------------------------------------------

/** The 5 pieces of a set (helmet, body, gloves, pants, boots), from the item definitions. */
export function outfitPieces(set: SetId): string[] {
  return ARMOR_SLOTS.map((slot) => ITEM_TEMPLATES.find((t) => t.setId === set && t.slot === slot)?.id).filter((x): x is string => !!x);
}

/** Item rarities that count for an outfit: Uncommon counts for Common; the others only themselves. */
export function outfitItemRarities(r: OutfitRarity): Rarity[] {
  return r === 'common' ? ['common', 'uncommon'] : [r];
}

export interface OutfitProgress {
  outfit: OutfitRef;
  unlocked: boolean;
  /** Each piece of the set and whether the book has it in a rarity that counts. */
  pieces: { templateId: string; found: boolean }[];
  found: number;
  /** 4. 10. 2026 (package 3, C): unlocked by a season reward ("Season 1 champion"). */
  label?: string | null;
}

export function outfitProgress(collection: CollectionState, o: OutfitRef): OutfitProgress {
  const found = new Set(collection.found);
  const rarities = outfitItemRarities(o.rarity);
  const pieces = outfitPieces(o.set).map((templateId) => ({ templateId, found: rarities.some((r) => found.has(collectionKey(templateId, r) ?? '')) }));
  const n = pieces.filter((p) => p.found).length;
  // 4. 10. 2026 (package 3, C): a season reward unlocks it for the account; the book's fields stay as they are.
  const label = rewardOutfitLabel(collection, o);
  return { outfit: o, unlocked: !!label || (pieces.length === 5 && n === 5), pieces, found: n, ...(label ? { label } : {}) };
}

/** "Season 1 champion" when a season reward unlocked this outfit, else null. */
export function rewardOutfitLabel(collection: CollectionState | null | undefined, o: OutfitRef): string | null {
  return collection?.rewards?.find((r) => r.set === o.set && r.rarity === o.rarity)?.label ?? null;
}

export function isOutfitUnlocked(collection: CollectionState, o: OutfitRef): boolean {
  return outfitProgress(collection, o).unlocked;
}

/** Every unlocked outfit, in `ALL_OUTFITS` order. Worked out from the book, never stored. */
export function unlockedOutfits(collection: CollectionState): OutfitRef[] {
  return ALL_OUTFITS.filter((o) => isOutfitUnlocked(collection, o));
}

// ---- Match my gear (20. 10. 2026, owner) -------------------------------------------------------
// The default look: the portrait shows the outfit of the set the survivor wears the most armor pieces of (at least 3), in
// the rarity most of those pieces have, but only an outfit the survivor has unlocked (outfits stay a Collection reward):
// at most the highest unlocked rarity of that set; none of that set's outfits unlocked, the base look. Cosmetic only.
// Tie-breaks: between sets most pieces, then the higher rarity, then the set order (`OUTFIT_SET_IDS`; with five armor
// slots two sets can never both have 3, so they only matter if the rule changes); between rarities of the pieces the one
// most pieces have, a tie goes to the higher one. A broken piece still counts (it is on the body).

/** How the portrait picks its outfit: `gear` follows the worn armor, `manual` is the wardrobe's choice. */
export const LOOK_MODES = ['gear', 'manual'] as const;
export type LookMode = (typeof LOOK_MODES)[number];

export const GEAR_LOOK_RULES = {
  /** Pieces of one set it takes. */
  minPieces: 3,
} as const;

/** Worn gear as the look needs it: each slot's template and rarity (`Equipment` and the client's items fit it). */
export type WornGear = Partial<Record<GearSlot, { templateId: string; rarity: Rarity } | null | undefined>>;

/** What the worn armor points at, before the unlock cap: the set, the rarity most of its pieces have, how many. */
export interface GearLook {
  set: SetId;
  rarity: OutfitRarity;
  pieces: number;
}

const OUTFIT_RANK: Record<OutfitRarity, number> = { common: 0, rare: 1, epic: 2, legendary: 3 };

/** The outfit rarity whose look an item rarity shares: Uncommon looks Common (as in the book's rule). */
export function outfitRarityOf(r: Rarity): OutfitRarity {
  return r === 'uncommon' ? 'common' : r;
}

/** The mode a survivor is in. Missing: `manual` when an outfit was chosen before the option came, else `gear`. */
export function lookModeOf(a: Pick<Appearance, 'look' | 'wardrobe'> | null | undefined): LookMode {
  if (a?.look === 'gear' || a?.look === 'manual') return a.look;
  return isOutfit(a?.wardrobe) ? 'manual' : 'gear';
}

/** The rarity most of the pieces have; a tie goes to the higher rarity. */
function majorityRarity(rs: OutfitRarity[]): OutfitRarity {
  let best: OutfitRarity = rs[0];
  let bestN = 0;
  for (const r of OUTFIT_RARITIES) {
    const n = rs.filter((x) => x === r).length;
    // `>=` while going up: an equal count of a higher rarity wins.
    if (n > 0 && n >= bestN) {
      best = r;
      bestN = n;
    }
  }
  return best;
}

/** The set and rarity the worn armor points at (null: no set with `minPieces` worn pieces). */
export function gearLook(worn: WornGear | null | undefined): GearLook | null {
  if (!worn) return null;
  const bySet = new Map<SetId, OutfitRarity[]>();
  for (const slot of ARMOR_SLOTS) {
    const it = worn[slot];
    const set = it ? findItemTemplate(it.templateId)?.setId : undefined;
    if (!it || !set || !(OUTFIT_SET_IDS as readonly string[]).includes(set)) continue;
    bySet.set(set, [...(bySet.get(set) ?? []), outfitRarityOf(it.rarity)]);
  }
  let best: GearLook | null = null;
  // In set order, so an equal set later in the list never replaces an earlier one.
  for (const set of OUTFIT_SET_IDS) {
    const rs = bySet.get(set);
    if (!rs || rs.length < GEAR_LOOK_RULES.minPieces) continue;
    const c: GearLook = { set, rarity: majorityRarity(rs), pieces: rs.length };
    if (!best || c.pieces > best.pieces || (c.pieces === best.pieces && OUTFIT_RANK[c.rarity] > OUTFIT_RANK[best.rarity])) best = c;
  }
  return best;
}

/**
 * The outfit "Match my gear" shows for a gear look: the unlocked outfit of that set nearest the pieces' rarity, never
 * above it while a lower one is unlocked (the highest unlocked at or below it); when only higher ones are unlocked, the
 * lowest of them (20. 10. 2026: the owner's "only unlocked outfits"; that last case is our own call, see
 * docs/notes/looks.md). Null: the base look.
 */
export function matchedOutfit(look: GearLook | null | undefined, unlocked: (o: OutfitRef) => boolean): OutfitRef | null {
  if (!look) return null;
  const have = OUTFIT_RARITIES.filter((rarity) => unlocked({ set: look.set, rarity }));
  if (!have.length) return null;
  const below = have.filter((r) => OUTFIT_RANK[r] <= OUTFIT_RANK[look.rarity]);
  return { set: look.set, rarity: below.length ? below[below.length - 1] : have[0] };
}

/** What the portrait shows: in `gear` mode the matched outfit, in `manual` the chosen one while it is still unlocked. */
export function lookOutfit(a: Pick<Appearance, 'look' | 'wardrobe'> | null | undefined, worn: WornGear | null | undefined, unlocked: (o: OutfitRef) => boolean): OutfitRef | null {
  if (lookModeOf(a) === 'gear') return matchedOutfit(gearLook(worn), unlocked);
  const w = a?.wardrobe;
  return isOutfit(w) && unlocked(w) ? { set: w.set, rarity: w.rarity } : null;
}

/** `unlocked` for a list of unlocked outfits (the client has `unlockedOutfits`). */
export const unlockedIn =
  (list: readonly OutfitRef[]) =>
  (o: OutfitRef): boolean =>
    list.some((x) => x.set === o.set && x.rarity === o.rarity);
